/**
 * PhotoCapture — button -> file picker (mobile camera on phones).
 *
 * Props:
 *  - onCapture: (file) => Promise<void>
 *  - disabled: boolean
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function PhotoCapture({ onCapture, disabled = false }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  function triggerPicker() {
    if (disabled || isUploading) return;
    inputRef.current?.click();
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);
    try {
      await onCapture(file);
    } catch (err) {
      setError(err.message || t('brainDump.uploadFailed', 'Upload fehlgeschlagen'));
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="flex flex-col items-center">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
        data-testid="photo-input"
      />
      <button
        type="button"
        onClick={triggerPicker}
        disabled={disabled || isUploading}
        className={`
          w-28 h-28 md:w-32 md:h-32 rounded-full font-medium text-white shadow-lg transition-all
          flex items-center justify-center text-5xl
          ${isUploading
            ? 'bg-[var(--aif-color-warning)]'
            : 'bg-[var(--aif-color-success)] hover:opacity-90 active:scale-95'}
          ${(disabled || isUploading) ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        aria-label={t('brainDump.takePhoto', 'Foto aufnehmen')}
        data-testid="photo-capture-btn"
      >
        {isUploading ? '⏳' : '📷'}
      </button>
      <div className="mt-2 text-sm text-[var(--aif-text-muted)] text-center">
        {isUploading
          ? t('brainDump.uploading', 'Laedt hoch...')
          : t('brainDump.takePhoto', 'Foto aufnehmen')}
      </div>
      {error && (
        <div className="mt-2 text-xs text-[var(--aif-color-danger)] bg-[var(--aif-color-danger-light)] px-2 py-1 rounded">
          {error}
        </div>
      )}
    </div>
  );
}
